import { Alert, FlexColumn, Link } from '@elementary/common-ui/components';
import useViewErrorsTracking from '@elementary/common-ui/hooks/tracking/useViewErrorsTracking';
import { extractApiContext } from '@elementary/common-ui/utils/errorTracking';
import { Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
import useSupportChat from 'shared/hooks/useSupportChat';

const DEFAULT_ERROR_MESSAGE =
  'An internal error occurred creating your ticket.';

type Props = {
  error: Error | null;
  provider: 'jira' | 'linear';
  contextId?: string;
  integrationId: string;
};

const TicketCreationError = ({
  error,
  provider,
  contextId,
  integrationId,
}: Props) => {
  const supportChat = useSupportChat();
  const { pathname } = useLocation();

  const apiContext = error ? extractApiContext(error) : undefined;
  const component =
    provider === 'linear' ? 'CreateLinearTicketError' : 'CreateJiraTicketError';

  useViewErrorsTracking({
    error: error || undefined,
    errorName: 'ApiError',
    context: {
      component,
      page: 'CreateTicketDialog',
      appState: {
        currentRoute: pathname,
        ...(contextId && { contextId }),
        integrationId,
      },
      ...(apiContext && { api: apiContext }),
      tags: ['create-ticket', 'api-error', provider],
    },
  });

  const parseErrorMessage = () => {
    if (!error) return '';

    try {
      const errorObj = error as any;
      let extraError =
        errorObj?.response?.data?.extra?.error ||
        errorObj?.response?.data?.extra?.errorMessage;

      if (extraError && typeof extraError === 'string') {
        const errorsMatch = extraError.match(/errors":\s*(.+)/i);
        if (errorsMatch && errorsMatch[1]) {
          return errorsMatch[1].trim().replaceAll(/[{}]/g, '');
        }
        return 'Errors: ' + extraError;
      }

      const responseMessage = errorObj?.response?.data?.message;
      if (responseMessage) {
        return responseMessage;
      }
      return error.message || '';
    } catch (e) {
      return '';
    }
  };

  return (
    <Alert title={DEFAULT_ERROR_MESSAGE} severity="error">
      <FlexColumn gap={1}>
        <Typography>{parseErrorMessage()}</Typography>
        <Typography>
          Please try again or{' '}
          <Link onClick={supportChat.show} href="#">
            contact us
          </Link>{' '}
          if the error persists.
        </Typography>
      </FlexColumn>
    </Alert>
  );
};

export default TicketCreationError;
